import { Box, FilterListBox, Flex, IllustratedMessage, ListBox, Truncate } from '@eleveo/components-react';
import { TFunction } from 'i18next';
import React from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useCollator } from 'react-aria';
import { useTranslation } from 'react-i18next';

import {
  PTO_AND_SHIFT_SWAP_REQUEST_SUPERVISOR_OPTIONS,
  REQUEST_STATE_OPTIONS_TO_FILTER,
} from '../../../config/agentsRequests';
import { QueueChannelType } from '../../../graphql/components';
import { AcdQueuesResult, TimezonesUsedInQueuesResult } from '../../../graphql/types';
import { HistoricalDataInterval } from '../../../scenes/DataImport/utils';
import { KpiOptionComponent } from '../../../scenes/Reports/ReportsAdherenceScene';
import { KPI_OPTIONS_TO_FILTER } from '../../../scenes/Reports/config';
import { isNonNullable } from '../../../utils/guards';
import { shouldShowSearchField } from '../../../utils/helpers';
import { formatTimezoneIdToName } from '../../../utils/timezoneFormatter';
import { RequestStatusComponent, RequestSubtypeComponent } from '../../AgentsRequests';

import type {
  AccordionItemBaseProps,
  AccordionProps,
  FilterListBoxProps,
  Option,
  OptionItemBase,
} from '@eleveo/components-react';

export const channelTypeItems = (t: TFunction) =>
  Object.values(QueueChannelType).map((type) => ({
    id: type,
    name: t(`queue.channelType.${type}`),
  }));

export const historicalDataIntervalItems = (t: TFunction) =>
  Object.values(HistoricalDataInterval).map((interval) => ({
    id: interval.toString(),
    name: t(`dataImport.historicalDataInterval.${interval}`),
  }));

export const AcdQueueComponent = ({ name, source, t }: { name: string; source: string; t: TFunction }) => (
  <Flex column>
    <Truncate>{name}</Truncate>
    <Truncate>
      {t('queue.queueId')}: <span className="font-bold">{source}</span>
    </Truncate>
  </Flex>
);

export const acdQueueItems = (t: TFunction, acdQueuesResult: AcdQueuesResult) =>
  acdQueuesResult.data?.acdQueues.map(({ id, queueName, acdSourceQueueId }) => {
    const isDuplicateName = acdQueuesResult.data?.acdQueues.find(
      (option) => option.queueName === queueName && option.id !== id
    );

    return {
      id,
      name: isDuplicateName ? <AcdQueueComponent name={queueName} source={acdSourceQueueId} t={t} /> : queueName,
      textValue: isDuplicateName ? `${queueName} (${acdSourceQueueId})` : queueName,
    };
  }) ?? [];

export const timezoneItems = (data: TimezonesUsedInQueuesResult) =>
  data.data?.queuesTimezones.map(({ zoneId }) => ({
    id: zoneId,
    name: formatTimezoneIdToName(zoneId),
  })) ?? [];

// TODO: Include/extend with Shift swap subtypes in future
export const subtypeItems = (t: TFunction) =>
  PTO_AND_SHIFT_SWAP_REQUEST_SUPERVISOR_OPTIONS.map(({ subtype, nameKey }) => ({
    id: subtype,
    textValue: t(nameKey),
    name: <RequestSubtypeComponent type={subtype} />,
  }));

export const stateItems = (t: TFunction) =>
  REQUEST_STATE_OPTIONS_TO_FILTER.map(({ type, nameKey }) => ({
    id: type,
    textValue: t(nameKey),
    name: <RequestStatusComponent status={type} />,
  }));

export const kpiItems = (t: TFunction) =>
  KPI_OPTIONS_TO_FILTER.map(({ type, nameKey }) => ({
    id: type,
    textValue: t(nameKey),
    name: <KpiOptionComponent type={type} />,
  }));

/**
 * Returns a flat map of items even when items have children/sections.
 */
export const getItemsMap = (items: CheckboxFilterTileProps['items']) => {
  const map = new Map<string | number, OptionItemBase>();

  const buildMap = (data: CheckboxFilterTileProps['items']) => {
    data.forEach((d) => {
      if (d.children) {
        buildMap(d.children);
      } else {
        map.set(d.id, d);
      }
    });
  };

  buildMap(items);

  return map;
};

export const sortItemsByProperty = (
  collator: Intl.Collator,
  items: CheckboxFilterTileProps['items'],
  property: string
) => {
  return (
    items
      .map((item) => {
        if (item.children) {
          // @ts-expect-error: Option is section
          // eslint-disable-next-line no-param-reassign
          item.children = sortItemsByProperty(collator, item.children, property);
        }

        return item;
      })
      // @ts-expect-error: Option is item
      .sort((a, b) => collator.compare(a[property], b[property]))
  );
};

// Use in Accordion Header Tile with CheckboxFilterTile as component
export const getCheckboxFilterTileSubtitle = (
  t: TFunction,
  items: CheckboxFilterTileProps['items'],
  selectedKeys: string[]
) => {
  const count = selectedKeys.length;

  if (count === 0) {
    return undefined;
  }

  const firstItem = getItemsMap(items).get(selectedKeys[0]);
  const subtitle = firstItem ? firstItem.textValue || (firstItem.name as string) : undefined;

  return count === 1 ? subtitle : t('filter.selected', { count });
};

export const getActiveFilterValue = (items: CheckboxFilterTileProps['items'] | undefined, selectedKeys: string[]) => {
  const count = selectedKeys.length;

  if (count === 0) {
    return '';
  }

  const map = getItemsMap(items ?? []);

  return selectedKeys
    .map((id) => map.get(id))
    .filter(isNonNullable)
    .map((item) => item.textValue || item.name)
    .join(', ');
};

export type CheckboxFilterTileProps =
  // Accordion item props
  AccordionProps<AccordionItemBaseProps>['items'][number] &
    // ListBox props and adjusted props
    Omit<FilterListBoxProps<Option<OptionItemBase>>, 'selectionMode' | 'onSelectionChange'> &
    Partial<Pick<FilterListBoxProps<Option<OptionItemBase>>, 'selectionMode'>> & {
      onSelectionChange: (keys: string[]) => void;
    } & {
      //  Custom props
      sortByField?: null | string; // TODO: would be better to keep it generic?
    };

export const CheckboxFilterTile = ({
  items,
  testId,
  onSelectionChange,
  selectionMode = 'multiple',
  sortByField = 'name',
  title,
  ...props
}: CheckboxFilterTileProps) => {
  const { t } = useTranslation();
  const collator = useCollator();

  const count = getItemsMap(items).size;

  // TODO: Will be part of the components soon.
  let content = <IllustratedMessage heading={t('filter.noValuesAvailable')} isSmall className="m-auto" />;

  if (count > 0) {
    const shouldRenderSearch = shouldShowSearchField(count);
    const sortedItems = sortByField ? sortItemsByProperty(collator, items, sortByField) : items;

    const listProps = {
      ...props,
      'aria-label': title,
      items: sortedItems,
      onSelectionChange: (keys: Set<string | number>) => onSelectionChange([...keys] as string[]),
      selectionMode,
      testId: `${testId}:filter`,
    };

    content = shouldRenderSearch ? <FilterListBox {...listProps} /> : <ListBox {...listProps} />;
  }

  return <Box padding={20}>{content}</Box>;
};
